/*
 * Какой это браузер и какое устройство.
 *
 * Нужно ровно для одного: показать человеку шаги установки приложения именно
 * для его браузера. Ни для чего другого этим знанием пользоваться не стоит —
 * возможности проверяются наличием возможностей, а не названием браузера.
 *
 * Разбираем строку браузера, а не `navigator.userAgentData`: строку легко
 * подменить в проверках и прогнать на десятке настоящих значений, не поднимая
 * десяток браузеров. Точности здесь хватает — мы всего лишь выбираем, какую
 * инструкцию показать, и ошибку человек поправит списком «у меня другой».
 */

export type BrowserName =
	| 'safari'
	| 'chrome'
	| 'edge'
	| 'firefox'
	| 'opera'
	| 'yandex'
	| 'samsung'
	| 'vivaldi'
	| 'brave'
	/** Встроенное окно внутри чужого приложения: ни установки, ни уведомлений. */
	| 'in-app'
	| 'unknown';

export type PlatformName = 'ios' | 'android' | 'windows' | 'macos' | 'linux' | 'unknown';

export interface BrowserInfo {
	browser: BrowserName;
	platform: PlatformName;
	/** Основная версия браузера. Ноль, если её из строки не достать. */
	version: number;
}

/** Приложения, чьи встроенные окна встречаются чаще всего. */
const IN_APP = /FBAN|FBAV|Instagram|Line\/|MicroMessenger|OKApp|VKClient|TikTok|Snapchat/i;

export function isIOSDevice(userAgent: string, touchPoints: number): boolean {
	// iPad с iPadOS 13+ выдаёт себя за настольный Mac; отличаем по касаниям.
	return /iPhone|iPod/.test(userAgent) || (/iPad|Macintosh/.test(userAgent) && touchPoints > 1);
}

function detectPlatform(userAgent: string, touchPoints: number): PlatformName {
	if (isIOSDevice(userAgent, touchPoints)) return 'ios';
	if (/Android/i.test(userAgent)) return 'android';
	if (/Windows/i.test(userAgent)) return 'windows';
	if (/Macintosh|Mac OS X/i.test(userAgent)) return 'macos';
	if (/Linux|X11|CrOS/i.test(userAgent)) return 'linux';
	return 'unknown';
}

/**
 * Настоящий ли это Safari, а не окно внутри чужого приложения.
 *
 * На iOS такое окно выдаёт себя двумя способами: приложение подписывается в
 * строке браузера, либо в строке нет «Safari/», которое настоящий Safari
 * ставит всегда.
 */
function looksLikeInAppBrowser(userAgent: string, platform: PlatformName): boolean {
	if (IN_APP.test(userAgent)) return true;
	if (platform !== 'ios') return false;
	// Свои браузеры iOS честно называются CriOS/FxiOS/EdgiOS и Safari не пишут.
	if (/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser/.test(userAgent)) return false;
	return !/Safari\//.test(userAgent);
}

function majorVersion(userAgent: string, patterns: RegExp[]): number {
	for (const pattern of patterns) {
		const match = userAgent.match(pattern);
		if (match?.[1]) return Number.parseInt(match[1], 10);
	}
	return 0;
}

/**
 * Порядок проверок важен: Яндекс, Samsung, Edge, Opera, Vivaldi и Brave все
 * несут в строке «Chrome», поэтому ищутся раньше него. Safari — последним:
 * его подпись есть почти у каждого браузера на движке WebKit и Blink.
 */
function detectName(userAgent: string, platform: PlatformName, brave: boolean): BrowserName {
	if (looksLikeInAppBrowser(userAgent, platform)) return 'in-app';

	if (/YaBrowser|YaSearchBrowser/i.test(userAgent)) return 'yandex';
	if (/SamsungBrowser/i.test(userAgent)) return 'samsung';
	if (/Edg(A|iOS)?\//i.test(userAgent)) return 'edge';
	if (/OPR\/|OPiOS|Opera/i.test(userAgent)) return 'opera';
	if (/Vivaldi/i.test(userAgent)) return 'vivaldi';
	// Brave намеренно не пишет о себе в строке — узнаём по собственному
	// свойству, которое он добавляет в navigator.
	if (brave) return 'brave';
	if (/CriOS/i.test(userAgent)) return 'chrome';
	if (/FxiOS|Firefox/i.test(userAgent)) return 'firefox';
	if (/Chrome|Chromium/i.test(userAgent)) return 'chrome';
	if (/Safari\//i.test(userAgent)) return 'safari';
	return 'unknown';
}

export function detectBrowser(userAgent: string, touchPoints: number, brave = false): BrowserInfo {
	const platform = detectPlatform(userAgent, touchPoints);
	const browser = detectName(userAgent, platform, brave);

	return {
		browser,
		platform,
		version: majorVersion(userAgent, [
			/Version\/(\d+)/, // Safari называет свою версию так
			/(?:Edg|EdgA|EdgiOS)\/(\d+)/,
			/(?:YaBrowser|SamsungBrowser|OPR|Vivaldi|FxiOS|CriOS)\/(\d+)/,
			/Firefox\/(\d+)/,
			/Chrome\/(\d+)/
		])
	};
}

/** То же, но про браузер, в котором приложение открыто прямо сейчас. */
export function currentBrowser(): BrowserInfo {
	if (typeof navigator === 'undefined') {
		return { browser: 'unknown', platform: 'unknown', version: 0 };
	}

	const brave = Boolean((navigator as Navigator & { brave?: unknown }).brave);
	return detectBrowser(navigator.userAgent, navigator.maxTouchPoints ?? 0, brave);
}
